import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ArrowLeft,
  Upload,
  Search,
  Loader2,
  RefreshCw,
  Trash2,
  CheckCircle,
  X,
  BookOpen,
  Globe,
  Tag,
  MoreHorizontal,
  Power,
  SlidersHorizontal,
  Eye,
  Pencil,
  Brain,
  Sparkles,
  ExternalLink,
  Lock,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import { Badge } from "@foundation/components/ui/badge";
import { Label } from "@foundation/components/ui/label";
import { Textarea } from "@foundation/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@foundation/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@foundation/components/ui/command";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@foundation/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@foundation/components/ui/dropdown-menu";
import { useToast } from "@foundation/hooks/use-toast";
import ScopingAILayout from "../components/RevisrLayout";
import { Link } from "react-router-dom";
import { useUser } from "@foundation/contextapi/UserContext";
import { useSuperAdmin } from "../hooks/useSuperAdmin";
import {
  assignTags as assignRevisrTags,
  createTag as createRevisrTag,
} from "../../../knowledge-base-overrides/services/knowledgeTagsService";
import type { ContextCategory } from "../services/contextCategoryService";
import {
  fetchContextCategories,
  fetchDocCategoryMapping,
  saveDocCategoryMapping,
  removeDocCategoryMapping,
} from "../services/contextCategoryService";
import { adminLibraryService } from "../services/adminLibraryService";
import {
  fetchKnowledgeDocsWithTags,
  analyzeKnowledgeDocument,
  updateKnowledgeDoc,
  deleteKnowledgeDoc,
  uploadKnowledgeDocument,
} from "../services/knowledgeDocsService";
import type { KnowledgeDoc } from "../services/knowledgeDocsService";
import type { AdminLibraryItem, AdminLibraryTag, AdminLibraryStatus, TagCategory } from "../types/adminLibrary";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@foundation/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@foundation/components/ui/dialog";
import { DocumentPreviewModal } from "../components/DocumentPreviewModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function knowledgeDocToLibraryItem(doc: KnowledgeDoc): AdminLibraryItem {
  return {
    id: doc.id,
    title: (doc.metadata?.title as string) || doc.doc_name,
    description: (doc.metadata?.description as string) || null,
    markdown_content: null,
    markdown_url: null,
    file_type: doc.doc_name.split(".").pop() || "document",
    file_size: null,
    status: ((doc.metadata?.status as string) || "published") as AdminLibraryStatus,
    created_by: doc.user_id,
    created_at: doc.created_at,
    updated_at: doc.created_at,
    metadata: {},
    tags: doc.revisrTags || [],
  };
}

function getDocTitle(doc: KnowledgeDoc): string {
  return (doc.metadata?.title as string) || doc.doc_name;
}

function getDocDescription(doc: KnowledgeDoc): string {
  return (doc.metadata?.description as string) || "";
}

function getDocStatus(doc: KnowledgeDoc): AdminLibraryStatus {
  return ((doc.metadata?.status as string) || "published") as AdminLibraryStatus;
}

function getTagsByCategory(doc: KnowledgeDoc, category: TagCategory): AdminLibraryTag[] {
  return doc.revisrTags?.filter((t) => t.category === category) || [];
}

// ─── TagsCell ─────────────────────────────────────────────────────────────────

function TagsCell({ tags }: { tags: AdminLibraryTag[] }) {
  const [open, setOpen] = useState(false);

  if (tags.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {tags.slice(0, 2).map((tag) => (
        <Badge key={tag.id} variant="outline" className="text-xs bg-gray-100 text-gray-800 border-gray-200">
          {tag.name}
        </Badge>
      ))}
      {tags.length > 2 && (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold hover:bg-gray-100 cursor-pointer bg-transparent"
            >
              +{tags.length - 2}
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              className="z-[200] w-auto rounded-md border bg-popover p-2 text-popover-foreground shadow-md outline-none max-w-[260px] animate-in fade-in-0 zoom-in-95"
            >
              <p className="text-xs text-muted-foreground mb-1.5 font-medium">All tags</p>
              <div className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <Badge key={tag.id} variant="outline" className="text-xs bg-gray-100 text-gray-800 border-gray-200">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ScopingKnowledgeBase() {
  const { toast } = useToast();
  const { user } = useUser();
  const { isSuperAdmin } = useSuperAdmin();

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        // @ts-expect-error – @foundation alias resolved by Vite, not TypeScript
        const { supabase: sb } = await import("@foundation/utils/supabaseClient");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data } = await (sb as any)
          .schema('altzero')
          .from('base_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        setIsAdmin((data as { role?: string } | null)?.role === 'admin');
      } catch {
        setIsAdmin(false);
      }
    })();
  }, [user?.id]);

  // ── Document list state ────────────────────────────────────────────────────
  const [knowledgeDocs, setKnowledgeDocs] = useState<KnowledgeDoc[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);

  // ── Revisr tags (for filter UI) ────────────────────────────────────────────
  const [allTags, setAllTags] = useState<AdminLibraryTag[]>([]);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [adminSearchQuery, setAdminSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdminLibraryStatus | "all">("all");
  const [filterGeneralTags, setFilterGeneralTags] = useState<AdminLibraryTag[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [activeFilterCategory, setActiveFilterCategory] = useState<string>("tags");

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // ── Delete dialog ──────────────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  // ── Preview ────────────────────────────────────────────────────────────────
  const [previewItem, setPreviewItem] = useState<AdminLibraryItem | null>(null);

  // ── Edit dialog ────────────────────────────────────────────────────────────
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<KnowledgeDoc | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editVisibility, setEditVisibility] = useState("global");
  const [editStatus, setEditStatus] = useState<"draft" | "published">("published");
  const [editGeneralTags, setEditGeneralTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editTagsPopoverOpen, setEditTagsPopoverOpen] = useState(false);

  // ── Upload dialog state ────────────────────────────────────────────────────
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiAnalysisDone, setAiAnalysisDone] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [uploadTagInput, setUploadTagInput] = useState("");
  const [uploadVisibility, setUploadVisibility] = useState<"private" | "org" | "global">("global");
  const [uploadStatus, setUploadStatus] = useState<"draft" | "published">("published");
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [uploadContextCategoryId, setUploadContextCategoryId] = useState("");
  const [uploadOntology, setUploadOntology] = useState<{ entityTypes: Array<{ name: string; description: string }>; relationshipTypes: Array<{ name: string; fromType: string; toType: string }> } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── KB categories (for upload category/subcategory dropdowns) ─────────────
  type KBCategory = { id: string; name: string; displayName: string; subcategories: string[] };
  const [kbCategories, setKbCategories] = useState<KBCategory[]>([]);

  // ── Context categories (for doc unlock mapping) ────────────────────────────
  const [contextCategories, setContextCategories] = useState<ContextCategory[]>([]);
  const [editContextCategoryId, setEditContextCategoryId] = useState<string>('');
  const [isLoadingEditMapping, setIsLoadingEditMapping] = useState(false);

  // ── Load documents ─────────────────────────────────────────────────────────
  const loadKnowledgeDocs = useCallback(async () => {
    setIsLoadingDocs(true);
    try {
      const docs = await fetchKnowledgeDocsWithTags();
      setKnowledgeDocs(docs);
    } catch (error) {
      console.error("Error loading knowledge docs:", error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setIsLoadingDocs(false);
    }
  }, [toast]);

  // ── Load Revisr tags (for filter dropdowns) ────────────────────────────────
  const loadTags = useCallback(async () => {
    try {
      const tags = await adminLibraryService.listTags();
      setAllTags(tags);
    } catch (error) {
      console.error("Error loading tags:", error);
    }
  }, []);

  // ── Load KB categories ──────────────────────────────────────────────────────
  const loadCategories = useCallback(async () => {
    try {
      const { data: { session } } = await (await import("@foundation/utils/supabaseClient")).supabase.auth.getSession();
      const token = session?.access_token ?? "";
      const orgId = (await import("../../../header/activeWorkspace")).getActiveWorkspaceId() ?? "";
      const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const res = await fetch(`${API_BASE}/api/knowledge/categories`, {
        headers: { Authorization: `Bearer ${token}`, "x-user-id": session?.user?.id ?? "", "x-org-id": orgId },
      });
      if (res.ok) {
        const json = await res.json();
        setKbCategories(json.categories || []);
      }
    } catch (err) {
      console.warn("Failed to load categories:", err);
    }
  }, []);

  // ── Load context categories ────────────────────────────────────────────────
  useEffect(() => {
    fetchContextCategories()
      .then(setContextCategories)
      .catch((err) => console.warn('Failed to load context categories:', err));
  }, []);

  useEffect(() => {
    if (user) {
      loadKnowledgeDocs();
      loadTags();
      loadCategories();
    }
  }, [user, loadKnowledgeDocs, loadTags, loadCategories]);

  // Re-fetch documents when the active workspace changes
  useEffect(() => {
    const handleWorkspaceChanged = () => {
      loadKnowledgeDocs();
    };
    window.addEventListener('workspace-changed', handleWorkspaceChanged);
    return () => window.removeEventListener('workspace-changed', handleWorkspaceChanged);
  }, [loadKnowledgeDocs]);

  // ── Tags grouped by category for filter UI ────────────────────────────────
  const tagsByCategory = useMemo(() => {
    const grouped: Record<string, AdminLibraryTag[]> = {
      general: [],
      standard: [],
    };
    allTags.forEach((tag) => {
      if (grouped[tag.category]) grouped[tag.category].push(tag);
    });
    return grouped;
  }, [allTags]);

  // ── Filtered docs (client-side) ────────────────────────────────────────────
  const filteredDocs = useMemo(() => {
    let result = knowledgeDocs;

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((doc) => getDocStatus(doc) === statusFilter);
    }

    // Search filter
    if (adminSearchQuery.trim()) {
      const q = adminSearchQuery.toLowerCase();
      result = result.filter(
        (doc) =>
          getDocTitle(doc).toLowerCase().includes(q) ||
          getDocDescription(doc).toLowerCase().includes(q) ||
          doc.tags.some((t) => t.toLowerCase().includes(q)) ||
          doc.revisrTags?.some((t) => t.name.toLowerCase().includes(q)),
      );
    }

    // General tag filter
    if (filterGeneralTags.length > 0) {
      result = result.filter((doc) =>
        filterGeneralTags.every((ft) =>
          doc.revisrTags?.some((t) => t.id === ft.id && t.category === "general"),
        ),
      );
    }

    return result;
  }, [knowledgeDocs, statusFilter, adminSearchQuery, filterGeneralTags]);

  // ── Paginated docs ─────────────────────────────────────────────────────────
  const paginatedDocs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredDocs.slice(start, start + PAGE_SIZE);
  }, [filteredDocs, currentPage]);

  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const totalActiveFilters =
    filterGeneralTags.length +
    (statusFilter !== "all" ? 1 : 0);

  // ── Publish toggle ─────────────────────────────────────────────────────────
  const handlePublishToggle = async (doc: KnowledgeDoc) => {
    const currentStatus = getDocStatus(doc);
    const newStatus = currentStatus === "published" ? "draft" : "published";
    try {
      await updateKnowledgeDoc(doc.id, { metadata: { status: newStatus } });
      // Update local state instead of re-fetching
      setKnowledgeDocs((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? { ...d, metadata: { ...d.metadata, status: newStatus } }
            : d,
        ),
      );
      toast({
        title: "Success",
        description: `Document ${newStatus === "published" ? "activated" : "deactivated"} successfully`,
      });
    } catch (error) {
      console.error("Error toggling status:", error);
      toast({ title: "Error", description: "Failed to update document status", variant: "destructive" });
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleOpenEdit = (doc: KnowledgeDoc) => {
    setEditDoc(doc);
    setEditTitle(getDocTitle(doc));
    setEditDescription(getDocDescription(doc));
    setEditCategory(doc.category || "");
    setEditVisibility(doc.visibility || "global");
    const docStatus = getDocStatus(doc);
    setEditStatus(docStatus === "archived" ? "draft" : docStatus);
    const generalTagNames = doc.revisrTags?.filter((t) => t.category === "general").map((t) => t.name) || [];
    setEditGeneralTags(generalTagNames);
    setEditTagInput("");
    // Pre-populate context category mapping
    setEditContextCategoryId('');
    setIsLoadingEditMapping(true);
    fetchDocCategoryMapping(doc.id)
      .then((mapping) => {
        if (mapping) {
          setEditContextCategoryId(mapping.categoryId);
        }
      })
      .catch(() => {/* non-fatal */})
      .finally(() => setIsLoadingEditMapping(false));
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editDoc || !editTitle.trim()) return;
    setIsSavingEdit(true);
    try {
      // Build Revisr tag IDs
      const tagsToCreate: Array<{ name: string; category: TagCategory }> = [];
      editGeneralTags.forEach((t) => tagsToCreate.push({ name: t, category: "general" }));

      const tagIds: string[] = [];
      for (const tagData of tagsToCreate) {
        const existing = allTags.find(
          (t) => t.name.toLowerCase() === tagData.name.toLowerCase() && t.category === tagData.category,
        );
        if (existing) {
          tagIds.push(existing.id);
        } else {
          try {
            const newTag = await adminLibraryService.createTag({
              name: tagData.name.toLowerCase(),
              category: tagData.category,
            });
            tagIds.push(newTag.id);
          } catch {
            // tag may already exist; skip
          }
        }
      }

      await updateKnowledgeDoc(editDoc.id, {
        category: editCategory,
        visibility: editVisibility,
        metadata: {
          title: editTitle.trim(),
          description: editDescription.trim() || undefined,
          status: editStatus,
        },
        tag_ids: tagIds,
      });

      // Update local state instead of re-fetching
      setKnowledgeDocs((prev) =>
        prev.map((d) =>
          d.id === editDoc.id
            ? {
                ...d,
                category: editCategory,
                visibility: editVisibility,
                metadata: {
                  ...d.metadata,
                  title: editTitle.trim(),
                  description: editDescription.trim() || undefined,
                  status: editStatus,
                },
                revisrTags: tagsToCreate.map((t) => {
                  const existing = allTags.find(
                    (at) => at.name.toLowerCase() === t.name.toLowerCase() && at.category === t.category,
                  );
                  return existing ?? {
                    id: `kbtag-${t.name}`,
                    name: t.name,
                    category: t.category,
                    usage_count: 0,
                    created_at: d.created_at,
                  };
                }),
              }
            : d,
        ),
      );
      // Save context category mapping
      if (editDoc) {
        try {
          if (editContextCategoryId) {
            await saveDocCategoryMapping(editDoc.id, editContextCategoryId);
          } else {
            await removeDocCategoryMapping(editDoc.id).catch(() => {/* ignore if no mapping */});
          }
        } catch {
          // Non-fatal: mapping save failure shouldn't block document save
          console.warn('[handleSaveEdit] Failed to save category mapping');
        }
      }

      toast({ title: "Success", description: "Document updated successfully" });
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating document:", error);
      toast({ title: "Error", description: "Failed to update document", variant: "destructive" });
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDeleteDoc = async (id: string) => {
    try {
      await deleteKnowledgeDoc(id);
      // Remove from local state instead of re-fetching
      setKnowledgeDocs((prev) => prev.filter((d) => d.id !== id));
      toast({ title: "Success", description: "Document deleted successfully" });
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({ title: "Error", description: "Failed to delete document", variant: "destructive" });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // ── Upload: file selection ─────────────────────────────────────────────────
  const handleFileSelected = useCallback(async (file: File) => {
    setStagedFile(file);
    setAiAnalysisDone(false);
    setUploadTitle(file.name.replace(/\.[^/.]+$/, ""));
    setUploadCategory("");
    setUploadTags([]);
    setUploadDescription("");
    setUploadOntology(null);

    // Auto-trigger AI analysis
    setIsAnalyzing(true);
    try {
      const sampleText = await file.slice(0, 4000).text().catch(() => "");
      const analysis = await analyzeKnowledgeDocument(sampleText, file.name);
      if (analysis.category) setUploadCategory(analysis.category);
      if (analysis.tags.length > 0) setUploadTags(analysis.tags);
      if (analysis.ontology) setUploadOntology(analysis.ontology);
      setAiAnalysisDone(true);
    } catch (err) {
      console.warn("AI analysis failed:", err);
      setAiAnalysisDone(true);
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingUpload(false);
      if (isUploadingDoc) return;
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelected(file);
    },
    [isUploadingDoc, handleFileSelected],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelected(file);
      e.target.value = "";
    },
    [handleFileSelected],
  );

  // ── Upload: submit ─────────────────────────────────────────────────────────
  const handleKnowledgeUpload = async () => {
    if (!stagedFile) return;

    if (!uploadTitle.trim()) {
      toast({ title: "Title required", description: "Please enter a document title" });
      return;
    }

    setIsUploadingDoc(true);
    try {
      const result = await uploadKnowledgeDocument(stagedFile, {
        title: uploadTitle.trim(),
        description: uploadDescription.trim() || undefined,
        category: uploadCategory || "general",
        tags: uploadTags,
        visibility: uploadVisibility,
        status: uploadStatus,
      });

      const uploadedDocId = result.id ?? result.doc_id;

      // Create & assign Revisr library tags (general only — no industry/region)
      if (uploadedDocId) {
        const tagsToCreate: Array<{ name: string; category: TagCategory }> = [];
        uploadTags.forEach((t) => tagsToCreate.push({ name: t, category: "general" }));

        if (tagsToCreate.length > 0) {
          const tagIds: string[] = [];
          for (const tagData of tagsToCreate) {
            try {
              const tag = await createRevisrTag({ name: tagData.name.toLowerCase(), category: tagData.category });
              tagIds.push(tag.id);
            } catch {
              // ignore
            }
          }
          if (tagIds.length > 0) {
            await assignRevisrTags({ doc_id: uploadedDocId, tag_ids: tagIds });
          }
        }
      }

      // Save context category mapping if selected
      if (uploadedDocId && uploadContextCategoryId) {
        try {
          await saveDocCategoryMapping(uploadedDocId, uploadContextCategoryId);
        } catch {
          console.warn('[handleKnowledgeUpload] Failed to save category mapping');
        }
      }

      toast({ title: "Success", description: "Document uploaded successfully" });

      // Reset upload form
      setStagedFile(null);
      setUploadTitle("");
      setUploadDescription("");
      setUploadCategory("");
      setUploadTags([]);
      setUploadTagInput("");
      setUploadVisibility("global");
      setUploadStatus("published");
      setAiAnalysisDone(false);
      setUploadContextCategoryId("");
      setUploadOntology(null);
      setUploadDialogOpen(false);

      loadKnowledgeDocs();
      loadTags();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setIsUploadingDoc(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const getStatusBadgeColor = (status: AdminLibraryStatus) => {
    switch (status) {
      case "published": return "bg-green-100 text-green-800 border-green-200";
      case "draft": return "bg-orange-100 text-orange-800 border-orange-200";
      case "archived": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusDisplayLabel = (status: AdminLibraryStatus) => {
    switch (status) {
      case "published": return "Active";
      case "draft": return "Inactive";
      case "archived": return "Archived";
      default: return status;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <ScopingAILayout>
      <div className="w-full px-4 py-8 bg-background min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/revisr/resource-library"
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Revisr Library
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center">Revisr Library</h1>
              <p className="text-muted-foreground mt-1">
                Browse our comprehensive library of preloaded standard documents, standards and reference materials.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  onClick={() => setUploadDialogOpen(true)}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Upload
                </Button>
              )}
              <Button
                variant="outline"
                onClick={loadKnowledgeDocs}
                disabled={isLoadingDocs}
                className="gap-2"
              >
                {isLoadingDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {isLoadingDocs ? "Loading..." : "Refresh"}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* ── Documents Table ─────────────────────────────────────────────── */}
          <Card className="border-primary/25">
            <CardContent className="pt-6">
              {/* Search + Filter */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={adminSearchQuery}
                    onChange={(e) => { setAdminSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-10"
                  />
                </div>
                <Popover open={isFilterPanelOpen} onOpenChange={setIsFilterPanelOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <SlidersHorizontal className="h-4 w-4" />
                      Filters
                      {totalActiveFilters > 0 && (
                        <Badge variant="secondary" className="h-5 px-1.5 text-xs">{totalActiveFilters}</Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[520px] p-0" align="end">
                    <div className="flex h-[360px]">
                      {/* Left sidebar */}
                      <div className="w-[160px] border-r bg-muted/30 p-2 space-y-0.5 overflow-y-auto">
                        <button
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${activeFilterCategory === "tags" ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"}`}
                          onClick={() => setActiveFilterCategory("tags")}
                        >
                          Tags
                          {filterGeneralTags.length > 0 && (
                            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{filterGeneralTags.length}</Badge>
                          )}
                        </button>
                        {isSuperAdmin && (
                          <button
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${activeFilterCategory === "status" ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"}`}
                            onClick={() => setActiveFilterCategory("status")}
                          >
                            Status
                            {statusFilter !== "all" && (
                              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">1</Badge>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Right content */}
                      <div className="flex-1 flex flex-col">
                        <div className="px-4 py-3 border-b">
                          <h4 className="font-medium text-sm capitalize">{activeFilterCategory}</h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                          {activeFilterCategory === "tags" && (
                            <div className="space-y-1">
                              {tagsByCategory.general.length === 0 ? (
                                <p className="text-sm text-muted-foreground px-2 py-4 text-center">No tags available</p>
                              ) : (
                                tagsByCategory.general.map((tag) => {
                                  const isSelected = filterGeneralTags.some((t) => t.id === tag.id);
                                  return (
                                    <button
                                      key={tag.id}
                                      type="button"
                                      className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                                      onClick={() => {
                                        if (isSelected) setFilterGeneralTags(filterGeneralTags.filter((t) => t.id !== tag.id));
                                        else setFilterGeneralTags([...filterGeneralTags, tag]);
                                        setCurrentPage(1);
                                      }}
                                    >
                                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                      </div>
                                      <span className="capitalize">{tag.name}</span>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                          {activeFilterCategory === "status" && (
                            <div className="space-y-1">
                              {(["all", "published", "draft", "archived"] as const).map((s) => {
                                const label = s === "all" ? "All Status" : s === "published" ? "Active" : s === "draft" ? "Inactive" : "Archived";
                                return (
                                  <button
                                    key={s}
                                    type="button"
                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                                    onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                                  >
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${statusFilter === s ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                                      {statusFilter === s && <CheckCircle className="w-3 h-3 text-white" />}
                                    </div>
                                    <span>{label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="px-4 py-3 border-t flex items-center justify-between">
                          <button
                            type="button"
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => { setFilterGeneralTags([]); setStatusFilter("all"); setCurrentPage(1); }}
                          >
                            Clear all
                          </button>
                          <span className="text-sm text-muted-foreground">
                            {filteredDocs.length} result{filteredDocs.length !== 1 ? "s" : ""}
                          </span>
                          <Button size="sm" onClick={() => setIsFilterPanelOpen(false)}>Apply filters</Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Active filter chips */}
              {totalActiveFilters > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {statusFilter !== "all" && (
                    <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200 cursor-pointer" onClick={() => { setStatusFilter("all"); setCurrentPage(1); }}>
                      Status: {getStatusDisplayLabel(statusFilter as AdminLibraryStatus)}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  )}
                  {filterGeneralTags.map((tag) => (
                    <Badge key={tag.id} variant="outline" className="bg-gray-100 text-gray-800 border-gray-200 cursor-pointer" onClick={() => { setFilterGeneralTags(filterGeneralTags.filter((t) => t.id !== tag.id)); setCurrentPage(1); }}>
                      Tag: {tag.name}<X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                  <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => { setFilterGeneralTags([]); setStatusFilter("all"); setCurrentPage(1); }}>
                    Clear all
                  </button>
                </div>
              )}

              {/* Documents Table */}
              {isLoadingDocs ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="ml-2">Loading documents...</span>
                </div>
              ) : paginatedDocs.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No documents found</h3>
                  <p className="text-muted-foreground">
                    {isSuperAdmin ? "Upload your first document below to get started" : "No published documents available yet"}
                  </p>
                </div>
              ) : (
                <div>
                  <Table className="[&_tr]:border-primary/25">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[250px]">Title</TableHead>
                        {isSuperAdmin && <TableHead className="w-[100px]">Status</TableHead>}
                        <TableHead className="w-[120px]">Category</TableHead>
                        <TableHead className="w-[180px]">Tags</TableHead>
                        <TableHead className="w-[120px]">Created</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedDocs.map((doc) => {
                        const title = getDocTitle(doc);
                        const description = getDocDescription(doc);
                        const status = getDocStatus(doc);
                        const generalTags = getTagsByCategory(doc, "general");

                        return (
                          <TableRow key={doc.id}>
                            <TableCell>
                              <div className="min-w-0">
                                <p className="font-medium truncate">{title}</p>
                                {description && (
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">{description}</p>
                                )}
                                {doc.doc_name && title !== doc.doc_name && (
                                  <span className="text-xs text-muted-foreground">{doc.doc_name.split(".").pop()?.toUpperCase()}</span>
                                )}
                              </div>
                            </TableCell>
                            {isSuperAdmin && (
                              <TableCell>
                                <Badge variant="outline" className={`text-xs ${getStatusBadgeColor(status)}`}>
                                  {getStatusDisplayLabel(status)}
                                </Badge>
                              </TableCell>
                            )}
                            <TableCell>
                              {doc.category ? (
                                <span className="text-xs text-muted-foreground capitalize">{doc.category}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell><TagsCell tags={generalTags} /></TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">{formatDate(doc.created_at)}</span>
                            </TableCell>
                            <TableCell>
                              {isSuperAdmin ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem onClick={() => setPreviewItem(knowledgeDocToLibraryItem(doc))}>
                                      <Eye className="h-4 w-4 mr-2" />View
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleOpenEdit(doc)}>
                                      <Pencil className="h-4 w-4 mr-2" />Edit Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handlePublishToggle(doc)}>
                                      <Power className="h-4 w-4 mr-2" />
                                      {status === "published" ? "Deactivate" : "Activate"}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => { setItemToDelete(doc.id); setDeleteDialogOpen(true); }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <span
                                  className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground/60"
                                  title="Standard documents can only be viewed or downloaded by platform admins"
                                >
                                  <Lock className="h-4 w-4" />
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {filteredDocs.length > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length} documents
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</Button>
                        <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages}>Next</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Delete Confirmation ────────────────────────────────────────── */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete document?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the document and all its chunks from the knowledge base.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => itemToDelete && handleDeleteDoc(itemToDelete)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* ── Upload Document Dialog ────────────────────────────────────────── */}
      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!isUploadingDoc) {
            setUploadDialogOpen(open);
            if (!open) {
              setStagedFile(null);
              setUploadTitle("");
              setUploadDescription("");
              setUploadCategory("");
              setUploadTags([]);
              setUploadTagInput("");
              setUploadContextCategoryId("");

              setUploadOntology(null);
              setAiAnalysisDone(false);
            }
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2.5 text-base">
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[#33b864]/10 shrink-0">
                <Upload className="h-3.5 w-3.5 text-[#33b864]" />
              </div>
              Upload Standard Document
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingUpload(true); }}
              onDragLeave={() => setIsDraggingUpload(false)}
              onDrop={handleFileDrop}
              onClick={() => !stagedFile && fileInputRef.current?.click()}
              className={[
                "border-2 border-dashed rounded-xl px-6 py-8 text-center transition-colors cursor-pointer select-none",
                isDraggingUpload ? "border-[#33b864] bg-[#33b864]/5" : "border-border hover:border-[#33b864]/50",
              ].join(" ")}
            >
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileInputChange} accept=".pdf,.doc,.docx,.txt,.md" />
              {stagedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="h-5 w-5 text-[#33b864] flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">{stagedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(stagedFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button type="button" className="ml-auto" onClick={(e) => { e.stopPropagation(); setStagedFile(null); setAiAnalysisDone(false); setUploadOntology(null); }}>
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">Drop a file here or click to browse</p>
                  <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, TXT, MD</p>
                </div>
              )}
            </div>

            {/* AI analysis status */}
            {isAnalyzing && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#33b864]" />
                <span>Analysing document with AI…</span>
              </div>
            )}
            {aiAnalysisDone && !isAnalyzing && (
              <div className="flex items-center gap-2 text-xs text-[#33b864] px-1">
                <Brain className="h-3.5 w-3.5" />
                <span>AI analysis complete — fields pre-filled below</span>
              </div>
            )}

            {/* Fields — only shown once file is staged */}
            {stagedFile && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="upload-title">Document Title *</Label>
                      <Input id="upload-title" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="Enter document title" disabled={isUploadingDoc} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="upload-description">Description</Label>
                      <Textarea id="upload-description" value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Enter document description" rows={3} disabled={isUploadingDoc} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="upload-category">Category</Label>
                      <Input id="upload-category" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} placeholder="e.g. policy, safety, compliance" disabled={isUploadingDoc} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tags</Label>
                      <div className="flex flex-wrap gap-2">
                        {uploadTags.map((tag) => (
                          <Badge key={tag} variant="outline" className="bg-primary/10 text-primary border-primary/20 cursor-pointer" onClick={() => setUploadTags(uploadTags.filter((t) => t !== tag))}>
                            {tag}<X className="h-3 w-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                      <Input
                        placeholder="Type a tag and press Enter"
                        value={uploadTagInput}
                        onChange={(e) => setUploadTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === "Enter" || e.key === ",") && uploadTagInput.trim()) {
                            e.preventDefault();
                            const t = uploadTagInput.trim().toLowerCase();
                            if (!uploadTags.includes(t)) setUploadTags([...uploadTags, t]);
                            setUploadTagInput("");
                          }
                        }}
                        disabled={isUploadingDoc}
                      />
                    </div>
                  </div>

                  {/* Right */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Visibility</Label>
                      <Select value={uploadVisibility} onValueChange={(v) => setUploadVisibility(v as "private" | "org" | "global")} disabled={isUploadingDoc}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">All organisations (global)</SelectItem>
                          <SelectItem value="org">My organisation only</SelectItem>
                          <SelectItem value="private">Private (only me)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={uploadStatus} onValueChange={(v) => setUploadStatus(v as "draft" | "published")} disabled={isUploadingDoc}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="published">Active</SelectItem>
                          <SelectItem value="draft">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Context Category mapping */}
                    <div className="space-y-2 pt-1 border-t border-border">
                      <Label className="text-sm font-medium">Context Category</Label>
                      <p className="text-xs text-muted-foreground">
                        Organisations that complete this category's questionnaire will be able to select this document in policy generation.
                      </p>
                      <select
                        value={uploadContextCategoryId}
                        onChange={(e) => setUploadContextCategoryId(e.target.value)}
                        disabled={isUploadingDoc}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#33b864]/30 focus:border-[#33b864]/40 transition-all duration-200 disabled:opacity-50"
                      >
                        <option value="">No category (always available)</option>
                        {contextCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* AI Ontology (informational) */}
                    {uploadOntology && (uploadOntology.entityTypes.length > 0 || uploadOntology.relationshipTypes.length > 0) && (
                      <div className="space-y-2 pt-1 border-t border-border">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5 text-[#33b864]" />
                          <Label className="text-sm font-medium text-[#33b864]">AI-Detected Ontology</Label>
                        </div>
                        {uploadOntology.entityTypes.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Entity Types</p>
                            <div className="flex flex-wrap gap-1">
                              {uploadOntology.entityTypes.map((e) => (
                                <span key={e.name} title={e.description} className="text-[11px] px-2 py-0.5 rounded-full bg-muted border border-border text-foreground cursor-help">
                                  {e.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {uploadOntology.relationshipTypes.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Relationships</p>
                            <div className="flex flex-wrap gap-1">
                              {uploadOntology.relationshipTypes.map((r) => (
                                <span key={r.name} className="text-[11px] px-2 py-0.5 rounded-full bg-muted border border-border text-foreground">
                                  {r.fromType} → {r.name} → {r.toType}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-3 border-t border-border gap-2">
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={isUploadingDoc}>Cancel</Button>
            <Button
              onClick={handleKnowledgeUpload}
              disabled={isUploadingDoc || !stagedFile || !uploadTitle.trim()}
              className="bg-[#33b864] hover:bg-[#2da358] text-white"
            >
              {isUploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : "Upload Document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Document Preview Modal ─────────────────────────────────────────── */}
      <DocumentPreviewModal
        item={previewItem}
        onClose={() => setPreviewItem(null)}
      />

      {/* ── Edit Document Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!isSavingEdit) {
            setEditDialogOpen(open);
            if (!open) {
              setEditTagsPopoverOpen(false);
            }
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2.5 text-base">
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[#33b864]/10 shrink-0">
                <Pencil className="h-3.5 w-3.5 text-[#33b864]" />
              </div>
              Edit Document Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Document Title *</Label>
                  <Input
                    id="edit-title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Enter document title"
                    disabled={isSavingEdit}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter document description"
                    rows={3}
                    disabled={isSavingEdit}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-category">Category</Label>
                  <Input
                    id="edit-category"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    placeholder="e.g. policy, safety, compliance"
                    disabled={isSavingEdit}
                  />
                </div>
                <div className="space-y-2">
                  <Label>General Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {editGeneralTags.map((tag) => (
                      <Badge key={tag} variant="outline" className="bg-primary/10 text-primary border-primary/20 cursor-pointer" onClick={() => setEditGeneralTags(editGeneralTags.filter((t) => t !== tag))}>
                        {tag}<X className="h-3 w-3 ml-1" />
                      </Badge>
                    ))}
                  </div>

                  {/* Tags popover */}
                  <PopoverPrimitive.Root open={editTagsPopoverOpen} onOpenChange={setEditTagsPopoverOpen}>
                    <PopoverPrimitive.Trigger asChild>
                      <Button variant="outline" size="sm" className="w-full justify-start text-muted-foreground font-normal" disabled={isSavingEdit}>
                        <Tag className="h-4 w-4 mr-2" />Select existing tags...
                      </Button>
                    </PopoverPrimitive.Trigger>
                    <PopoverPrimitive.Content align="start" sideOffset={4} className="z-[150] w-64 rounded-md border bg-popover p-0 shadow-md">
                      <Command>
                        <CommandInput placeholder="Search tags..." />
                        <CommandList>
                          <CommandEmpty>No tags found.</CommandEmpty>
                          {tagsByCategory.general.length > 0 && (
                            <CommandGroup heading="General">
                              {tagsByCategory.general.map((tag) => {
                                const isSelected = editGeneralTags.includes(tag.name);
                                return (
                                  <CommandItem key={tag.id} onSelect={() => { isSelected ? setEditGeneralTags(editGeneralTags.filter((t) => t !== tag.name)) : setEditGeneralTags([...editGeneralTags, tag.name]); }}>
                                    <CheckCircle className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100 text-primary" : "opacity-0"}`} />
                                    <span className="capitalize">{tag.name}</span>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Root>

                  <Input
                    placeholder="Or type a new tag and press Enter"
                    value={editTagInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === ",") && editTagInput.trim()) {
                        e.preventDefault();
                        const t = editTagInput.trim().toLowerCase();
                        if (!editGeneralTags.includes(t)) setEditGeneralTags([...editGeneralTags, t]);
                        setEditTagInput("");
                      }
                    }}
                    disabled={isSavingEdit}
                  />
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select value={editVisibility} onValueChange={setEditVisibility} disabled={isSavingEdit}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">All organisations (global)</SelectItem>
                      <SelectItem value="org">My organisation only</SelectItem>
                      <SelectItem value="private">Private (only me)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editStatus} onValueChange={(v: string) => setEditStatus(v as "draft" | "published")} disabled={isSavingEdit}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Active</SelectItem>
                      <SelectItem value="draft">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Context Category mapping */}
                <div className="space-y-2 pt-1 border-t border-border">
                  <Label className="text-sm font-medium">Context Category</Label>
                  <p className="text-xs text-muted-foreground">
                    Organisations that complete this category's questionnaire will be able to select this document in policy generation.
                  </p>
                  {isLoadingEditMapping ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading mapping…
                    </div>
                  ) : (
                    <>
                      <select
                        value={editContextCategoryId}
                        onChange={(e) => setEditContextCategoryId(e.target.value)}
                        disabled={isSavingEdit}
                        className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#33b864]/30 focus:border-[#33b864]/40 transition-all duration-200 disabled:opacity-50"
                      >
                        <option value="">No category (always available)</option>
                        {contextCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-border gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={isSavingEdit}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSavingEdit || !editTitle.trim()}
              className="bg-[#33b864] hover:bg-[#2da358] text-white"
            >
              {isSavingEdit ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScopingAILayout>
  );
}
